// Center-column surface for influence tracing (see lib/influenceTracing.js).
//
// Renders in the main column beside the trace (which stays in the right
// column). As the agent builds the trail, every cited trace/prompt is a
// clickable ref that pops that trace into the right column via onOpenTrace
// — so the operator follows influence by jumping between traces while the
// trail stays put here.
//
// Each armed trace is its own first-class Explore tab (App's explorer
// strip owns the tabs; this view renders the active one), so tracing a
// new turn opens a brand-new tab and never destroys an in-progress one.
// Switching the active tab REMOUNTS this view, so a module-level
// RESULT_CACHE keeps a finished trace from re-running when you return.
//
// Content runs edge-to-edge: no side gutter here, the column gaps provide
// the separation.

import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import ScrollArea from "./ScrollArea.jsx";
import Select from "./Select.jsx";
import LaunchCard from "./LaunchCard.jsx";
import { NOTE_CLASS } from "../lib/ui.js";
import { getChatCreds } from "../lib/chatCreds.js";
import { getRankedModels, defaultTaskModelFor } from "../lib/modelCatalog.js";
import {
    runInfluenceTrace,
    FUZZINESS_LEVELS,
    readFuzziness,
    writeFuzziness,
} from "../lib/influenceTracing.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../lib/authTokenStore.js";

const EYEBROW = "text-[11px] leading-[1.08] tracking-[0.06em] uppercase font-medium text-[color:var(--eyebrow)]";
const SECTION = "text-[11px] leading-[1.08] tracking-[0.06em] uppercase font-medium text-[color:var(--muted)]";

const TIME_FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
function when(ts) {
    if (!ts) return "";
    const d = ts instanceof Date ? ts : new Date(ts);
    return Number.isNaN(d.getTime()) ? String(ts) : TIME_FMT.format(d);
}

// Module-level cache of completed influence reports, keyed by tab + turn
// + fuzziness + model. Each influence trace is a real Explore tab, and
// switching the active explorer tab REMOUNTS this view — without a cache,
// coming back to a finished trace would re-run the whole agent. Re-run
// bypasses it (and refreshes the entry). Bounded so a long session
// opening many traces can't grow it without limit (evict oldest).
const RESULT_CACHE = new Map();
const RESULT_CACHE_MAX = 50;
onAuthIdentityChange(() => RESULT_CACHE.clear());
function cacheReport(key, report) {
    RESULT_CACHE.set(key, report);
    if (RESULT_CACHE.size > RESULT_CACHE_MAX) {
        RESULT_CACHE.delete(RESULT_CACHE.keys().next().value);
    }
}

const TOOL_LABEL = {
    get_session_prompts: "Reading session transcript",
    analytics_query: "Querying the graph",
    analytics_describe_schema: "Inspecting the schema",
    expand_preview: "Paging results",
    return_result: "Assembling the trail",
};

// trace/prompt refs open in the right column; entity refs re-scope.
const KIND_TO_DIM = { session: "sessions", file: "files", command: "commands", host: "hosts", tool: "tools", skill: "skills", endpoint: "endpoints" };
function isOpenableTrace(ref) {
    return ref && (ref.kind === "trace" || ref.kind === "prompt") && ref.id;
}

const CONF = {
    high: { label: "high", cls: "text-[color:var(--green)] border-[color:var(--green)]" },
    medium: { label: "medium", cls: "text-[color:var(--ink)] border-[color:var(--line-strong)]" },
    low: { label: "low", cls: "text-[color:var(--muted-soft)] border-[color:var(--line)]" },
};
function ConfidenceBadge({ value }) {
    const c = CONF[value] || CONF.medium;
    return (
        <span className={`shrink-0 rounded-[2px] border px-[5px] py-[1px] text-[10px] leading-[1.2] uppercase tracking-[0.04em] ${c.cls}`}>
            {c.label}
        </span>
    );
}

function RefChip({ refObj, onOpenTrace, onScopeNavigate }) {
    if (!refObj || !refObj.id) return null;
    const openable = isOpenableTrace(refObj);
    const dim = KIND_TO_DIM[refObj.kind];
    const clickable = openable || (dim && onScopeNavigate);
    const onClick = openable
        ? () => onOpenTrace?.(refObj.id)
        : dim
            ? () => onScopeNavigate?.(dim, refObj.id)
            : undefined;
    return (
        <button
            type="button"
            disabled={!clickable}
            onClick={onClick}
            className={[
                "inline-flex items-center gap-[5px] rounded-[2px] border px-[7px] py-[3px] text-[11px] leading-[1.15] max-w-full min-w-0 transition-colors",
                clickable
                    ? "border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--ink)] hover:border-[color:var(--green)] hover:text-[color:var(--green)] cursor-pointer"
                    : "border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted-soft)] cursor-default",
            ].join(" ")}
        >
            <span className="shrink-0 text-[9px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">{refObj.kind || "ref"}</span>
            <span className="truncate min-w-0">{refObj.label || refObj.id}</span>
            {openable ? <span aria-hidden className="shrink-0 text-[color:var(--muted-soft)]">↗</span> : null}
        </button>
    );
}

function ReportBody({ report, onOpenTrace, onScopeNavigate }) {
    const influences = Array.isArray(report.influences) ? report.influences : [];
    const pivot = report.pivot && (report.pivot.label || report.pivot.ref) ? report.pivot : null;
    return (
        <div className="grid gap-[18px] min-w-0">
            {report.summary ? (
                <div className="whitespace-pre-wrap break-words text-[13.5px] leading-[1.6] text-[color:var(--ink)] min-w-0">{report.summary}</div>
            ) : null}

            {pivot ? (
                <div className="grid gap-[5px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px] min-w-0" style={{ boxShadow: "inset 2px 0 0 var(--green)" }}>
                    <span className={SECTION}>Turning point</span>
                    <div className="flex items-start gap-[8px] min-w-0">
                        <span className="text-[12.5px] leading-[1.45] text-[color:var(--ink)] break-words min-w-0">{pivot.label}</span>
                        {pivot.timestamp ? <span className="shrink-0 font-mono text-[10.5px] text-[color:var(--muted-soft)]">{when(pivot.timestamp)}</span> : null}
                    </div>
                    {pivot.ref ? <div className="min-w-0"><RefChip refObj={pivot.ref} onOpenTrace={onOpenTrace} onScopeNavigate={onScopeNavigate} /></div> : null}
                </div>
            ) : null}

            {influences.length ? (
                <div className="grid gap-[5px] min-w-0">
                    <span className={SECTION}>Influences by strength</span>
                    <div className="grid gap-[8px] min-w-0">
                        {influences.map((inf, i) => (
                            <div key={i} className="grid gap-[5px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[8px] min-w-0">
                                <div className="flex items-start justify-between gap-[8px] min-w-0">
                                    <div className="min-w-0">
                                        {inf.relation ? <span className="block text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] break-words">{inf.relation}</span> : null}
                                        <div className="text-[13px] leading-[1.45] text-[color:var(--ink)] break-words">{inf.label}</div>
                                    </div>
                                    <ConfidenceBadge value={inf.confidence} />
                                </div>
                                {inf.why ? <div className="text-[12px] leading-[1.5] text-[color:var(--muted)] break-words min-w-0">{inf.why}</div> : null}
                                {inf.ref ? <div className="min-w-0"><RefChip refObj={inf.ref} onOpenTrace={onOpenTrace} onScopeNavigate={onScopeNavigate} /></div> : null}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <span className={NOTE_CLASS}>No influences found. Try a looser setting.</span>
            )}

            {report.fuzziness_note ? (
                <div className="grid gap-[3px] border-t border-[color:var(--line-soft)] pt-[10px] min-w-0">
                    <span className={SECTION}>Confidence note</span>
                    <span className="text-[12px] leading-[1.5] text-[color:var(--muted)] break-words">{report.fuzziness_note}</span>
                </div>
            ) : null}
        </div>
    );
}

export default function InfluenceTraceView({ descriptor, onOpenTrace, onScopeNavigate, onClose }) {
    const authContextEpoch = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
    const { trace, turn, turnIndex } = descriptor || {};
    // Persisted preference (localStorage), like the task model — the chosen
    // fuzziness sticks across traces and reloads. Default for a brand-new
    // browser is "balanced".
    const [fuzziness, setFuzzinessState] = useState(readFuzziness);
    const setFuzziness = (id) => { writeFuzziness(id); setFuzzinessState(id); };
    // Explicit-spend gate: the trace opens ARMED (a LaunchCard) and only
    // runs on the button. `armed` flips false on launch / on a cache hit
    // (a tab returning to an already-computed result). `runTick` forces a
    // fresh run for Re-run (which also clears the cache entry first).
    const [armedState, setArmedState] = useState(() => ({ authContextEpoch, value: true }));
    const armed = armedState.authContextEpoch === authContextEpoch ? armedState.value : true;
    const setArmed = (value) => setArmedState({ authContextEpoch, value });
    const [runTick, setRunTick] = useState(0);
    const [state, setState] = useState({ status: "idle", report: null, error: null, lastText: null });
    const [activity, setActivity] = useState([]);

    // Model selector — mirrors X-Ray Describe. Defaults to the cheap task
    // model (the catalog is one click away to bump to the flagship). The
    // agent still runs at the "primary" role, so picking a flagship gets
    // that model's default thinking depth. Gating the run on creds (below)
    // means the first run already uses the resolved model, so there's no
    // wasted re-run when creds resolve after the trace.
    const [creds, setCreds] = useState(null);
    useEffect(() => {
        let on = true;
        getChatCreds().then((c) => { if (on) setCreds(c); }).catch(() => { /* unauth/offline */ });
        return () => { on = false; };
    }, []);
    const [modelChoice, setModelChoice] = useState("");
    const defaultModelId = creds ? (creds.taskModel || defaultTaskModelFor(creds.provider) || creds.model) : "";
    const influenceModel = modelChoice || defaultModelId;
    const modelOptions = useMemo(() => {
        if (!creds?.provider) return [];
        const ranked = getRankedModels(creds.provider).map(({ model }) => ({
            value: model.id,
            label: model.contextWindow ? `${model.name} · ${Math.round(model.contextWindow / 1000)}k` : model.name,
        }));
        // A configured model outside the catalog (custom id) still has to be selectable.
        return ranked.some((o) => o.value === influenceModel) || !influenceModel
            ? ranked
            : [{ value: influenceModel, label: influenceModel }, ...ranked];
    }, [creds?.provider, influenceModel]);
    const modelLabel = modelOptions.find((o) => o.value === influenceModel)?.label || influenceModel;

    const cacheKey = `${authContextEpoch}:${descriptor?.key || trace?.id}:${turnIndex}:${fuzziness}:${influenceModel}`;
    const visibleState = state.authContextEpoch === authContextEpoch
        ? state
        : { status: "idle", report: null, error: null, lastText: null, authContextEpoch };

    useEffect(() => {
        if (!trace || !turn || !creds?.provider) return undefined;
        // Already computed (this turn + fuzziness + model) — e.g. a tab
        // returning after a switch. Show it and leave armed off so input
        // changes re-run rather than re-arm. Re-run clears the entry first,
        // so it falls through to a fresh run below.
        if (RESULT_CACHE.has(cacheKey)) {
            if (armed) setArmed(false);
            setState({ status: "done", report: RESULT_CACHE.get(cacheKey), error: null, lastText: null, authContextEpoch });
            setActivity([]);
            return undefined;
        }
        // Still armed (never launched this mount) — wait for the button.
        // Pre-launch fuzziness/model changes land here and just re-arm.
        if (armed) {
            setState({ status: "idle", report: null, error: null, lastText: null, authContextEpoch });
            return undefined;
        }
        const ctrl = new AbortController();
        setState({ status: "running", report: null, error: null, lastText: null, authContextEpoch });
        setActivity([]);
        const onEvent = (event) => {
            if (!event) return;
            if (event.type === "tool_execution_start") {
                const label = TOOL_LABEL[event.toolName] || event.toolName || "Working";
                setActivity((a) => [...a.slice(-11), { label, status: "running" }]);
            } else if (event.type === "tool_execution_end") {
                setActivity((a) => {
                    if (!a.length) return a;
                    const next = a.slice();
                    next[next.length - 1] = { ...next[next.length - 1], status: event.isError ? "error" : "done" };
                    return next;
                });
            }
        };
        runInfluenceTrace({ trace, turn, turnIndex, fuzziness, model: influenceModel || null, signal: ctrl.signal, onEvent }).then((res) => {
            if (ctrl.signal.aborted) return;
            if (res.status === "done") {
                cacheReport(cacheKey, res.report);
                setState({ status: "done", report: res.report, error: null, lastText: null, authContextEpoch });
            } else if (res.status !== "aborted") {
                setState({ status: "error", report: null, error: res.error, lastText: res.lastText, authContextEpoch });
            }
        }).catch((e) => {
            // runInfluenceTrace is contracted to resolve, never reject —
            // this is a backstop so a stray rejection can't leave the view
            // stuck on "running" forever.
            if (!ctrl.signal.aborted) setState({ status: "error", report: null, error: e, lastText: null, authContextEpoch });
        });
        return () => ctrl.abort();
    }, [cacheKey, creds?.provider, armed, runTick]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!trace) {
        return (
            <div className="grid h-full min-h-0 min-w-0 place-items-center bg-[color:var(--bg)] px-[14px]">
                {descriptor?.error ? (
                    <span role="alert" className="font-mono text-[11px] leading-[1.4] text-[color:var(--red,#c0392b)] break-words text-center">{descriptor.error}</span>
                ) : (
                    <span className={`${NOTE_CLASS} animate-pulse`}>Loading trace…</span>
                )}
            </div>
        );
    }
    const turnNo = typeof turnIndex === "number" ? turnIndex + 1 : null;
    const total = trace.turns?.length || null;
    const { status } = visibleState;

    // Run-shaping controls — reused in the LaunchCard (armed) and the header
    // (post-run), only one of which renders at a time.
    const modelSelect = modelOptions.length ? (
        <Select
            value={influenceModel}
            onChange={(v) => setModelChoice(String(v))}
            options={modelOptions}
            ariaLabel="Model for the influence trace"
            align="right"
            triggerStyle={{
                // Pin to the LaunchCard CTA's fixed 30px height so the trigger and
                // button line up exactly (inline-flex items-center centers the label).
                height: 30, minHeight: 0,
                fontSize: 11,
                backgroundColor: "var(--bg)",
                backgroundImage: "linear-gradient(var(--control-bg),var(--control-bg))",
            }}
            className="px-[9px]"
        />
    ) : null;
    const fuzzinessControl = (
        <div className="flex items-center gap-[8px] flex-wrap min-w-0">
            <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">Pivot fuzziness</span>
            <div className="inline-flex rounded-[2px] border border-[color:var(--line)] overflow-hidden">
                {FUZZINESS_LEVELS.map((lvl, i) => (
                    <button
                        key={lvl.id}
                        type="button"
                        onClick={() => setFuzziness(lvl.id)}
                        className={[
                            "px-[8px] py-[3px] text-[11px] leading-[1.1] transition-colors cursor-pointer",
                            i > 0 ? "border-l border-[color:var(--line)]" : "",
                            fuzziness === lvl.id
                                ? "bg-[color:var(--control-bg)] text-[color:var(--green)] font-medium"
                                : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                        ].join(" ")}
                    >
                        {lvl.label}
                    </button>
                ))}
            </div>
            <span className={`${NOTE_CLASS} min-w-0 break-words`}>{(FUZZINESS_LEVELS.find((l) => l.id === fuzziness) || {}).note}</span>
        </div>
    );

    const launch = () => setArmed(false);
    const rerun = () => { RESULT_CACHE.delete(cacheKey); setArmed(false); setRunTick((t) => t + 1); };

    return (
        <div className="grid h-full min-h-0 min-w-0 grid-rows-[auto_minmax(0,1fr)] bg-[color:var(--bg)]">
            {/* Header — edge to edge. Run-shaping controls live here only
                AFTER launch; while armed they're in the LaunchCard below so
                you set the lens before spending. */}
            <div className="grid gap-[7px] border-b border-[color:var(--line-soft)] pt-[12px] pb-[10px] min-w-0">
                <div className="flex items-center justify-between gap-[10px] min-w-0">
                    <div className={EYEBROW}>Influence trace</div>
                    {!armed ? (
                        <div className="flex shrink-0 items-center gap-[8px]">
                            <button type="button" onClick={rerun} disabled={status === "running"}
                                className="text-[11px] leading-[1.08] tracking-[0.06em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-40 disabled:cursor-default transition-colors cursor-pointer whitespace-nowrap">
                                Re-run
                            </button>
                            {modelSelect}
                        </div>
                    ) : null}
                </div>
                <h2 className="m-0 text-[15px] leading-[1.18] font-medium tracking-[-0.03em] text-[color:var(--ink)] truncate min-w-0">
                    {turnNo ? `Turn ${turnNo}${total ? ` / ${total}` : ""}` : "Turn"} · {trace.summary ? trace.summary.slice(0, 80) : trace.id}
                </h2>
                {!armed ? fuzzinessControl : null}
            </div>

            {/* Body — edge to edge */}
            <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[14px] py-[14px] min-w-0">
                {armed ? (
                    <LaunchCard
                        eyebrow="Influence trace"
                        description="Choose the lens, then trace this turn through the session transcript and analytics graph."
                        controls={
                            <>
                                {fuzzinessControl}
                                {modelSelect ? (
                                    <div className="flex items-center gap-[8px] min-w-0">
                                        <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">Model</span>
                                        {modelSelect}
                                    </div>
                                ) : null}
                            </>
                        }
                        ctaLabel="Trace influence"
                        onLaunch={launch}
                        disabled={!creds?.provider}
                        disabledHint={creds && !creds.provider ? "Configure a chat model first." : null}
                    />
                ) : status === "running" ? (
                    <div className="grid gap-[8px] min-w-0">
                        <span className={`${NOTE_CLASS} animate-pulse`}>
                            Tracing influence with {modelLabel || "the chat model"}. Reading transcripts and the analytics graph…
                        </span>
                        {activity.length ? (
                            <div className="grid gap-[2px] min-w-0">
                                {activity.map((a, i) => (
                                    <div key={i} className="flex items-center gap-[7px] text-[11.5px] leading-[1.5] min-w-0">
                                        <span className={[
                                            "shrink-0",
                                            a.status === "done" ? "text-[color:var(--green)]" : a.status === "error" ? "text-[color:var(--red,#c0392b)]" : "text-[color:var(--muted-soft)]",
                                        ].join(" ")}>
                                            {a.status === "done" ? "✓" : a.status === "error" ? "✕" : "•"}
                                        </span>
                                        <span className={a.status === "running" ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}>{a.label}</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : status === "error" ? (
                    <div className="grid justify-items-start gap-[8px] min-w-0">
                        <span className="font-mono text-[11px] leading-[1.4] text-[color:var(--red,#c0392b)] break-words min-w-0">
                            {visibleState.error?.message || String(visibleState.error || "The agent returned nothing.")}
                        </span>
                        {visibleState.lastText ? <span className="whitespace-pre-wrap break-words text-[11px] leading-[1.45] text-[color:var(--muted-soft)] min-w-0">{visibleState.lastText.slice(0, 800)}</span> : null}
                        <button type="button" onClick={rerun}
                            className="text-[11px] leading-[1.08] tracking-[0.06em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors cursor-pointer">
                            Retry
                        </button>
                    </div>
                ) : status === "done" && visibleState.report ? (
                    <ReportBody report={visibleState.report} onOpenTrace={onOpenTrace} onScopeNavigate={onScopeNavigate} />
                ) : null}
            </ScrollArea>
        </div>
    );
}
